import { resolveLexicalSubmissionContext } from '#pplx/frontend/apps/ask/ask-input/query-serialization/lexicalNodeToQuery.js';
import { useHasStartedTyping, useInputActions, useStartTypingTime, useUserInput, useUserInputJson, } from '#pplx/frontend/apps/ask/stores/queryInput.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useEffect, useMemo, useRef } from 'react';
export function useAskInputInstance({ onSubmit, immediate = true }) {
    const userInput = useUserInput();
    const userInputJson = useUserInputJson();
    const hasStartedTyping = useHasStartedTyping();
    const startTypingTime = useStartTypingTime();
    const { setUserInput, setHasStartedTyping, setStartTypingTime, markInputAsSubmitted } = useInputActions();
    const submittedRef = useRef(false);
    useEffect(() => {
        return () => {
            if (submittedRef.current) {
                // Rely on a cleanup effect to mark the input as submitted since
                // it's representative of when the input is no longer in use and
                // other components, like the suggestion dropdown, are being
                // unmounted and won't render a blank state due to this cleanup
                markInputAsSubmitted();
                submittedRef.current = false;
            }
        };
    }, [markInputAsSubmitted, immediate]);
    const handleSubmit = useCallback((params) => {
        logger.info('ask input submitted');
        const timeFromFirstType = startTypingTime ? performance.now() - startTypingTime : undefined;
        const semanticContext = resolveLexicalSubmissionContext({
            rootNode: params.json?.root,
            mentions: params.mentions,
            prefills: params.prefills,
            slashCommand: params.slashCommand,
            slashCommandSubmittedLabel: params.slashCommandSubmittedLabel,
        });
        const submitParams = {
            timeFromFirstType,
            ...params,
            ...semanticContext,
        };
        const wasSubmitRejected = onSubmit?.(submitParams) === false;
        if (wasSubmitRejected) {
            // The caller declined this submit, so avoid marking the input as submitted.
            return false;
        }
        if (immediate)
            markInputAsSubmitted();
        else
            submittedRef.current = true;
        return true;
    }, [immediate, markInputAsSubmitted, onSubmit, startTypingTime]);
    const handleChange = useCallback((newInput, json) => {
        // After submission, ignore stale Lexical onChange events that would
        // re-persist cleared input back to sessionStorage.
        // However, if the user starts typing new input, allow it through.
        if (submittedRef.current) {
            if (!newInput)
                return;
            submittedRef.current = false;
        }
        if (!hasStartedTyping && newInput) {
            setHasStartedTyping(true);
        }
        if (!startTypingTime) {
            setStartTypingTime(performance.now());
        }
        setUserInput(newInput, json);
    }, [hasStartedTyping, setUserInput, setHasStartedTyping, startTypingTime, setStartTypingTime]);
    return [
        userInput,
        userInputJson,
        useMemo(() => ({
            onChange: handleChange,
            onSubmit: handleSubmit,
            setUserInput,
        }), [handleChange, handleSubmit, setUserInput]),
    ];
}
//# sourceMappingURL=useAskInputInstance.js.map